import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/App';
import PortalHeader, { isPortalLoggedIn, getPortalToken } from './PortalHeader';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Calendar, 
  MapPin, 
  Loader2,
  Clock,
  CreditCard,
  ChevronRight,
  XCircle,
  AlertTriangle,
  CheckCircle2,
  IndianRupee
} from 'lucide-react';

const MyBookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  
  // Initialize portal settings from cache to prevent flash
  const [portalSettings, setPortalSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('portal_settings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.primary_color && parsed.secondary_color) {
          return parsed;
        }
      }
    } catch (e) {}
    return {
      portal_name: 'Book Your Stay',
      primary_color: '#d4a373',
      secondary_color: '#2d3748'
    };
  });
  
  // Cancellation state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  useEffect(() => {
    if (!isPortalLoggedIn()) {
      navigate('/login?returnUrl=/my-bookings');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = getPortalToken();
      
      const [settingsRes, bookingsRes] = await Promise.all([
        api.get('/portal/settings'),
        api.get(`/portal/bookings?token=${token}`)
      ]);
      
      const newSettings = { ...portalSettings, ...settingsRes.data };
      setPortalSettings(newSettings);
      localStorage.setItem('portal_settings_cache', JSON.stringify(newSettings));
      setBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_guest');
        navigate('/login?returnUrl=/my-bookings');
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      reserved: { label: 'Confirmed', variant: 'default', className: 'bg-green-500' },
      pending_approval: { label: 'Pending', variant: 'secondary', className: 'bg-yellow-500' },
      checked_in: { label: 'Checked In', variant: 'default', className: 'bg-blue-500' },
      checked_out: { label: 'Completed', variant: 'secondary', className: 'bg-gray-500' },
      cancelled: { label: 'Cancelled', variant: 'destructive', className: 'bg-red-500' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary', className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const canCancelBooking = (status) => {
    return !['cancelled', 'checked_in', 'checked_out'].includes(status);
  };

  const handleCancelClick = async (booking) => {
    setSelectedBooking(booking);
    setCancellationReason('');
    setCancellationPolicy(null);
    setCancelDialogOpen(true);
    
    // Fetch cancellation policy
    setLoadingPolicy(true);
    try {
      const token = getPortalToken();
      const res = await api.get(`/portal/bookings/${booking.id}/cancellation-policy?token=${token}`);
      setCancellationPolicy(res.data);
    } catch (error) {
      console.error('Failed to fetch cancellation policy:', error);
      toast.error('Failed to load cancellation policy');
    } finally {
      setLoadingPolicy(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;
    
    setCancelling(true);
    try {
      const token = getPortalToken();
      const res = await api.post(
        `/portal/bookings/${selectedBooking.id}/cancel?token=${token}`,
        { reason: cancellationReason }
      );
      
      toast.success('Booking cancelled successfully');
      
      // Show refund details
      if (res.data.refund_details?.refund_amount > 0) {
        toast.info(`Refund of ₹${res.data.refund_details.refund_amount.toLocaleString('en-IN')} will be processed within 5-7 business days`);
      }
      
      setCancelDialogOpen(false);
      setSelectedBooking(null);
      
      // Refresh bookings
      fetchData();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PortalHeader portalSettings={portalSettings} showBackButton backPath="/portal" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader portalSettings={portalSettings} showBackButton backPath="/portal" />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Bookings</h1>
        
        {bookings.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No bookings yet</h3>
              <p className="text-gray-500 mb-6">Start exploring and book your perfect stay</p>
              <Button 
                onClick={() => navigate('/')}
                style={{ backgroundColor: portalSettings.primary_color }}
              >
                Browse Properties
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`booking-card-${booking.id}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    {/* Booking Info */}
                    <div className="flex-1 p-4 sm:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">
                            {booking.property_name}
                          </h3>
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <MapPin className="h-4 w-4" />
                            {booking.property_address}
                          </p>
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mt-4">
                        <div>
                          <p className="text-gray-500">Check-in</p>
                          <p className="font-medium">{formatDate(booking.check_in)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Check-out</p>
                          <p className="font-medium">{formatDate(booking.check_out)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Room</p>
                          <p className="font-medium">{booking.room_type}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Nights</p>
                          <p className="font-medium">{booking.nights}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <div className="text-sm">
                          <span className="text-gray-500">Booking ID: </span>
                          <span className="font-mono font-medium">{booking.booking_number}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: portalSettings.primary_color }}>
                            ₹{booking.total_amount?.toLocaleString('en-IN')}
                          </p>
                          {booking.advance_paid > 0 && (
                            <p className="text-xs text-gray-500">
                              Paid: ₹{booking.advance_paid?.toLocaleString('en-IN')}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Cancel Button - only show for cancellable bookings */}
                      {canCancelBooking(booking.status) && (
                        <div className="mt-4 pt-4 border-t flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                            onClick={() => handleCancelClick(booking)}
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancel Booking
                          </Button>
                        </div>
                      )}
                      
                      {/* Show cancellation info for cancelled bookings */}
                      {booking.status === 'cancelled' && booking.refund_amount !== undefined && (
                        <div className="mt-4 pt-4 border-t bg-red-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3">
                          <p className="text-sm text-red-700">
                            <span className="font-medium">Cancelled</span>
                            {booking.refund_amount > 0 && (
                              <span className="ml-2">• Refund: ₹{booking.refund_amount?.toLocaleString('en-IN')}</span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Cancellation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking?
            </DialogDescription>
          </DialogHeader>
          
          {loadingPolicy ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : cancellationPolicy ? (
            <div className="space-y-4">
              {/* Booking Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">{selectedBooking?.property_name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(selectedBooking?.check_in)} - {formatDate(selectedBooking?.check_out)}
                </p>
                <p className="text-sm text-gray-500">
                  Booking: {selectedBooking?.booking_number}
                </p>
              </div>
              
              {/* Refund Policy */}
              <div className={`rounded-lg p-4 ${
                cancellationPolicy.refund_percent === 100 ? 'bg-green-50 border border-green-200' :
                cancellationPolicy.refund_percent === 50 ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {cancellationPolicy.refund_percent === 100 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : cancellationPolicy.refund_percent === 50 ? (
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      cancellationPolicy.refund_percent === 100 ? 'text-green-800' :
                      cancellationPolicy.refund_percent === 50 ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      {cancellationPolicy.policy}
                    </p>
                    <p className={`text-sm mt-1 ${
                      cancellationPolicy.refund_percent === 100 ? 'text-green-700' :
                      cancellationPolicy.refund_percent === 50 ? 'text-yellow-700' :
                      'text-red-700'
                    }`}>
                      {cancellationPolicy.policy_detail}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Financial Summary */}
              {cancellationPolicy.advance_paid > 0 && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-medium">₹{cancellationPolicy.advance_paid?.toLocaleString('en-IN')}</span>
                  </div>
                  {cancellationPolicy.cancellation_charge > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Cancellation Charge</span>
                      <span>-₹{cancellationPolicy.cancellation_charge?.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t font-medium">
                    <span className="text-gray-900">Refund Amount</span>
                    <span className="text-green-600">₹{cancellationPolicy.refund_amount?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}
              
              {/* Cancellation Reason */}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for cancellation (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Please let us know why you're cancelling..."
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-500">
              Unable to load cancellation policy
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
            >
              Keep Booking
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelling || loadingPolicy || !cancellationPolicy?.can_cancel}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Confirm Cancellation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookings;
